import Step, {DecisionStep, Process, SequenceStep} from "../../steps/models/Step";
import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import CancellationFormComponent from "./CancellationForm.component";
import ProcessComponent from "../../steps/components/DecisionComponent/Decision.component";
import ReduceRateComponent from "./ReduceRate.component";
import DowngradeComponent from "./Downgrade.component";
import FreeAddonComponent from "./FreeAddon.component";
import UpgradeComponent from "./Upgrade.component";
import {withNamespaces} from "react-i18next";
import CancellationService from "../services/CancellationService";
import LoadingComponent from "../../forms/components/LoadingComponent/LoadingComponent";
import {AuthContext} from "../../member/components/AuthContext";
import MemberService from "../../member/sevices/MemberService";
import CancellationReasonComponent from "./CancellationReason.component";
import cancellationService from "../services/CancellationService";
import CancellationConfirmation from "./CancellationConfirmation";
import StepComponent from "../../steps/components/StepComponent/StepComponent";
import messageService, {MessageTypes} from "../../common/services/message.service";
import MoneyHelper from "../../../services/MoneyHelper";
import {CancellationReasons} from "../models/CancelForm";
import PopupComponent from "../../common/components/Popup/Popup.component";
import {CancellationPeriodType} from "../models/CancelDetails";

function cancellationSubmitErrorMessage(err, t, i18n) {
  const type = err.error_type || 'unknown';
  const key = `membership_cancellation_${type}_error`;
  if (i18n.exists(key)) {
    return t(key);
  }
  return t('membership_cancellation_unknown_error');
}

export const CancellationProcessContext = createContext({
  cancelDetails: undefined,
  setLoading: undefined,
  plans: undefined
});

const CancellationOffer = {
  "reduce_rate": "reduce_rate",
  "downgrade": "downgrade",
  "free_addon": "free_addon",
  "upgrade": "upgrade",
  "cancellation": "cancellation",
}

function shuffle(array) {
  let currentIndex = array.length;

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {

    // Pick a remaining element...
    let randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
}

const configureProcess = (member, offers) => {
  console.log('configureProcess', member, offers);
  const reduceRateStep = offers.reduce_rate && new Step({
    name: CancellationOffer.reduce_rate,
    component: ReduceRateComponent,
    canAccept: (data) => data.reduce_rate && data.reduce_rate.terms_accepted,
  });

  const downgradeStep = offers.downgrade && new Step({
    name: CancellationOffer.downgrade,
    component: DowngradeComponent,
    canAccept: () => true,
  });
  const freeAddon = offers.free_addon && new Step({
    name: CancellationOffer.free_addon,
    component: FreeAddonComponent,
    canAccept: (data) => data.free_addon && data.free_addon.terms_accepted,
  });
  const cancellationStep = new Step({
    name: CancellationOffer.cancellation,
    component: CancellationFormComponent,
    canAccept: (data) => {
      const isCooldown = offers && offers.cancellation && offers.cancellation.type === CancellationPeriodType.CooldownPeriod;
      return isCooldown || (data.cancellation && data.cancellation.terms_accepted);
    },
  });
  const upgradeStep = offers.upgrade && new Step({
    name: CancellationOffer.upgrade,
    component: UpgradeComponent,
    canAccept: (data) => true,
  });
  const options = [
    new SequenceStep(CancellationReasons.CantAfford, CancellationReasons.CantAfford, [
      reduceRateStep,
      downgradeStep,
      freeAddon,
      cancellationStep,
    ].filter(step => !!step)),
    new SequenceStep(CancellationReasons.AccessToFeature, CancellationReasons.AccessToFeature, [
      upgradeStep,
      reduceRateStep,
      cancellationStep,
    ].filter(step => !!step)),
    new SequenceStep(CancellationReasons.NotAchievingGoals, CancellationReasons.NotAchievingGoals, [
      freeAddon,
      upgradeStep,
      reduceRateStep,
      cancellationStep,
    ].filter(step => !!step)),
    new SequenceStep(CancellationReasons.NotUsing, CancellationReasons.NotUsing, [
      reduceRateStep,
      downgradeStep,
      cancellationStep,
    ].filter(step => !!step)),
    new SequenceStep(CancellationReasons.Staff, CancellationReasons.NotUsing, [
      reduceRateStep,
      downgradeStep,
      cancellationStep,
    ].filter(step => !!step)),
    new SequenceStep(CancellationReasons.Cleanliness, CancellationReasons.NotUsing, [
      reduceRateStep,
      downgradeStep,
      cancellationStep,
    ].filter(step => !!step)),
    new SequenceStep(CancellationReasons.DontEnjoy, CancellationReasons.DontEnjoy, [
      reduceRateStep,
      cancellationStep,
    ].filter(step => !!step)),
  ];
  shuffle(options);

  return new Process(
    new DecisionStep('cancel_reason', 'cancel_reason', [
      ...options,
      new SequenceStep(CancellationReasons.Other, CancellationReasons.Other, [
        reduceRateStep,
        cancellationStep,
      ].filter(step => !!step))
    ], CancellationReasonComponent)
  );
};


const CancellationProcessComponent = (props) => {
  const [cancelDetails, setCancelDetails] = useState();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({}); //model to store data
  const {member, refreshMember} = useContext(AuthContext);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [process, setProcess] = useState(null);
  const processComponentRef = useRef();

  useEffect(() => {
    setLoading(true);
    CancellationService.getCancellationStatus().then(details => {
      console.log('setDetails', details);
      if (details.cancellation.error) {
        setError(props.t(`membership_cancellation_${details.cancellation.error}_error`))
      } else {
        setCancelDetails(details);
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (cancelDetails) {
      setProcess(configureProcess(member, cancelDetails));
    }
  }, [cancelDetails]);

  useEffect(() => {
    if (member) {
      MemberService.getCurrentMemberClubPlans().then(plans => {
        setPlans(plans);
      });
    }
  }, [member]);

  const change = (data) => {
    console.log('CATCH', JSON.stringify(data));
    setData(data)
  };

  const isValid = () => {
    const currentStep = process.getCurrentChild(data);
    console.log('isValid', currentStep);
    return !!data.cancel_reason && (!currentStep.canAccept || currentStep.canAccept(data.data || {}));
  };

  const isNotReasonStep = () => {
    if (!process) {
      return false;
    }
    const currentStep = process.getCurrentChild(data);
    return data.cancel_reason && currentStep.name !== 'cancel_reason';
  };

  const isCancellationStep = () => {
    if (!process) {
      return false;
    }
    const currentStep = process.getCurrentChild(data);
    console.log('isCancellationStep', currentStep);
    return currentStep.name === CancellationOffer.cancellation;
  };

  const onComplete = () => {
    console.log('On Complete');
  };

  const getOfferData = () => {
    const currentStep = process.getCurrentChild(data);
    return {
      cancel_reason: data.cancel_reason,
      accepted_offer: currentStep.name,
      data: data.data && data.data[currentStep.name] ? data.data[currentStep.name] : {},
    };
  };

  const acceptOffer = () => {
    if (!isNotReasonStep()) {
      console.log('Click Next!!!', processComponentRef);
      processComponentRef.current.next();
      return;
    }
    if (!isValid()) {
      console.warn("NOT VALID", data);
      return;
    }
    setLoading(true);
    setError(false);
    const offerData = getOfferData();
    cancellationService.submitCancellation(offerData).then(result => {
      console.log('RESULT', result);
      if (offerData.accepted_offer !== CancellationOffer.cancellation) {
        messageService.showMessage(MessageTypes.Success, props.t(`membership_cancellation_${offerData.accepted_offer}_accepted`))
        props.onEnd();
        refreshMember();
      } else {
        setSuccess(true);
      }
    }).catch(err => {
      setError(cancellationSubmitErrorMessage(err, props.t, props.i18n));
    }).finally(() => setLoading(false));
  };

  const close = () => {
    if (success) {
      refreshMember();
    }
    props.onEnd();
  };

  const getButtonText = () => {
    let text = '';
    if (isCancellationStep()) {
      if (cancelDetails.cancellation.isCooldownPeriod()) {
        text = props.t('cooldown_cancellation_request');
      } else if (!cancelDetails.cancellation.hasDues()) {
        text = props.t('confirm_cancellation_no_dues');
      } else {
        const offerData = getOfferData();
        const payNow = offerData.data.isPayNow ? offerData.data.isPayNow() : true;
        text = props.t(`pay_cancellation${payNow ? '_pay_now' : '_pay_regular'}`, {amount: MoneyHelper.toPrice(cancelDetails.cancellation.getFinalAmount(payNow))});
      }
    } else {
      text = props.t(isNotReasonStep() ? 'accept_offer' : 'continue')
    }

    return text;
  }

  return (
    <PopupComponent header={props.t(success ? 'cancellation_success_header' : 'cancellation_process_header')}
                    popupClasses={'cancellation-popup'}
                    onClose={close}>
      <CancellationProcessContext.Provider value={{cancelDetails: cancelDetails, setLoading: setLoading, plans: plans}}>
        {loading && <LoadingComponent isVisible={loading}/>}

        {cancelDetails && !cancelDetails.cancellation.error && (
          <div className={`cancellation-process-container ${isNotReasonStep() ? '' : 'cancellation-step-1'}`}>
            <StepComponent data={data} process={process} ended={success}/>
            {success ? <CancellationConfirmation cancelDetails={cancelDetails} offerData={getOfferData()}/> :
              process ? (
                <ProcessComponent process={process}
                                  ref={processComponentRef}
                                  data={data}
                                  setData={change}
                                  onComplete={onComplete}
                                  backText={props.t('back_cancellation')}
                                  nextText={isCancellationStep() ? null : props.t('continue_cancellation')}
                                  buttons={
                                    <React.Fragment>
                                      {!isNotReasonStep() && (
                                        <button className='form-main-button white-with-border close-button'
                                                onClick={props.onEnd}>
                                          {props.t('close')}
                                        </button>
                                      )}
                                      <button
                                        className={`form-main-button ${isCancellationStep() ? 'next-button' : ''}`}
                                        onClick={acceptOffer} disabled={!isValid()}>
                                        {getButtonText()}
                                      </button>
                                    </React.Fragment>
                                  }/>
              ) : null}
          </div>
        )}

        {error && <p className={`error-text mt-16 ${!cancelDetails ? 'no-details' : ''}`}
                     dangerouslySetInnerHTML={{__html: error}}/>}

      </CancellationProcessContext.Provider>
    </PopupComponent>
  );
};

CancellationProcessComponent.propTypes = {
  t: PropTypes.func.isRequired,
  onEnd: PropTypes.func.isRequired,
};

export default withNamespaces()(CancellationProcessComponent);